import React from 'react';

const DEFAULT_THRESHOLD = 40;
const DEFAULT_OVERSCAN = 8;

export interface TypeVirtualWindow {
    start: number;
    end: number;
    padTop: number;
    padBottom: number;
    active: boolean;
    measure: (id: string, height: number) => void;
}

export function useVirtualWindow(
    containerRef: React.RefObject<HTMLElement | null>,
    count: number,
    options: { estimate?: number; overscan?: number; threshold?: number } = {},
): TypeVirtualWindow {
    const { estimate = 64, overscan = DEFAULT_OVERSCAN, threshold = DEFAULT_THRESHOLD } = options;

    const active = count > threshold;

    const heights = React.useRef(new Map<string, number>());
    const [rowHeight, setRowHeight] = React.useState(estimate);
    const [range, setRange] = React.useState(() => ({
        start: 0,
        end: active ? Math.min(count, Math.ceil((window.innerHeight || 800) / estimate) + overscan * 2) : count,
    }));

    const measure = React.useCallback((id: string, height: number) => {
        if (!height || heights.current.get(id) === height) return;
        heights.current.set(id, height);
    }, []);

    React.useEffect(() => {
        if (!active) return;

        const known = [...heights.current.values()].sort((a, b) => a - b);
        if (!known.length) return;

        const median = known[Math.floor(known.length / 2)];
        setRowHeight((current) => (median && Math.abs(median - current) > 2 ? median : current));
    }, [active, range.start, range.end]);

    React.useEffect(() => {
        if (!active) {
            setRange({ start: 0, end: count });
            return;
        }

        const compute = () => {
            const element = containerRef.current;
            if (!element) return;

            const box = element.getBoundingClientRect();
            const viewport = window.innerHeight || 0;

            const hiddenAbove = Math.max(0, -box.top);
            const visibleRows = Math.ceil(viewport / rowHeight) + overscan * 2;

            const maxStart = Math.max(0, count - visibleRows);
            const start = Math.min(Math.max(0, Math.floor(hiddenAbove / rowHeight) - overscan), maxStart);
            const end = Math.min(count, start + visibleRows);

            setRange((previous) => (previous.start === start && previous.end === end ? previous : { start, end }));
        };

        compute();

        window.addEventListener('scroll', compute, { passive: true });
        window.addEventListener('resize', compute);

        return () => {
            window.removeEventListener('scroll', compute);
            window.removeEventListener('resize', compute);
        };
    }, [active, count, rowHeight, overscan, containerRef]);

    if (!active) {
        return { start: 0, end: count, padTop: 0, padBottom: 0, active: false, measure };
    }

    const start = Math.min(range.start, Math.max(0, count - 1));
    const end = Math.min(Math.max(range.end, start + 1), count);

    return {
        start,
        end,
        padTop: start * rowHeight,
        padBottom: Math.max(0, (count - end) * rowHeight),
        active: true,
        measure,
    };
}

export default useVirtualWindow;
